import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../client'
import { paraConsulta, type FiltrosDeChamado } from '../filtrosDeChamado'
import type {
  ApoioDaIaNoChamado,
  ApuracaoNaTela,
  CompetenciaComDado,
  DetalheChamado,
  Pagina,
  ConferenciaDeCalendario,
  MarcacaoParaGravar,
  ParecerDaIa,
  RegistroDeImportacao,
  RespostaDaCarga,
  ResumoChamado,
  SituacaoDaIa,
  SituacaoDaSincronizacao,
} from '../../types/chamado'

/**
 * As chaves de cache do módulo de chamados.
 *
 * Ficam aqui, e não em `queries/chaves.ts`, porque o que aquele arquivo existe para evitar é a
 * divergência entre quem consulta e quem invalida quando os dois estão em arquivos diferentes. No
 * módulo de chamados os escritores — a carga da planilha, a sincronização e o julgamento do fiscal —
 * estão neste mesmo arquivo, ao lado de todos os leitores. O prefixo comum `['chamados']` é o que faz `invalidateQueries` derrubar
 * lista, detalhe, competências, fechamento e histórico de uma vez.
 */
export const chaves = {
  /** Tudo de chamado. Invalidar isto derruba todas as consultas abaixo. */
  tudo: () => ['chamados'] as const,
  lista: (filtros: FiltrosDeChamado) => ['chamados', 'lista', filtros] as const,
  detalhe: (id: number) => ['chamados', 'detalhe', id] as const,
  competencias: () => ['chamados', 'competencias'] as const,
  fechamento: (competencia: string) => ['chamados', 'fechamento', competencia] as const,
  importacoes: () => ['chamados', 'importacoes'] as const,
  sincronizacao: () => ['chamados', 'sincronizacao'] as const,
  situacaoDaIa: () => ['chamados', 'analises-ia', 'situacao'] as const,
  pareceres: (id: number) => ['chamados', 'analises-ia', id] as const,
}

/**
 * Lista paginada, com os filtros que estão na URL.
 *
 * `placeholderData` mantém a página anterior visível durante a busca da próxima: sem isso a tabela
 * pisca, e a tabela vazia de meio segundo é lida como "acabou".
 *
 * @param filtros critérios correntes da tela
 * @returns a consulta da página
 */
export function useListaDeChamados(filtros: FiltrosDeChamado) {
  return useQuery({
    queryKey: chaves.lista(filtros),
    queryFn: async () =>
      (await api.get<Pagina<ResumoChamado>>('/chamados', { params: paraConsulta(filtros) })).data,
    placeholderData: (anterior) => anterior,
  })
}

/**
 * Detalhe de um chamado.
 *
 * @param id identificador interno (não o número do e-Niterói)
 * @returns a consulta do detalhe
 */
export function useChamado(id: number) {
  return useQuery({
    queryKey: chaves.detalhe(id),
    queryFn: async () => (await api.get<DetalheChamado>(`/chamados/${id}`)).data,
    enabled: Number.isFinite(id),
  })
}

/**
 * As competências que já têm chamado fechado, da mais recente para a mais antiga.
 *
 * É o que alimenta o seletor de competência das duas telas. Cada linha diz se a coleta cobre o mês
 * inteiro — e é essa informação, não o valor do indicador, que separa um fechamento apurável de um
 * número apurado sobre mês incompleto.
 *
 * @returns a consulta das competências
 */
export function useCompetenciasComDado() {
  return useQuery({
    queryKey: chaves.competencias(),
    queryFn: async () => (await api.get<CompetenciaComDado[]>('/chamados/competencias')).data,
  })
}

/**
 * O fechamento de uma competência: os seis indicadores do ANEXO VIII, com denominador e procedência.
 *
 * @param competencia mês de referência no formato `AAAA-MM`; vazio não consulta nada
 * @returns a consulta do fechamento
 */
export function useFechamentoDaCompetencia(competencia: string) {
  return useQuery({
    queryKey: chaves.fechamento(competencia),
    queryFn: async () => (await api.get<ApuracaoNaTela>(`/fiscalizacao/${competencia}`)).data,
    enabled: competencia.length > 0,
  })
}

/**
 * O histórico de cargas, da mais recente para a mais antiga.
 *
 * @param limite quantas linhas trazer
 * @returns a consulta do histórico
 */
export function useHistoricoDeCargas(limite = 20) {
  return useQuery({
    queryKey: [...chaves.importacoes(), limite] as const,
    queryFn: async () =>
      (await api.get<RegistroDeImportacao[]>('/chamados/importacoes', { params: { limite } })).data,
  })
}

/** O que a tela envia ao carregar a planilha. */
export interface EnvioDaPlanilha {
  arquivo: File
  /** Mês de referência; ausente, o servidor o deriva do nome do arquivo ("Ref AAAA-MM"). */
  competencia?: string
}

/**
 * Carrega a planilha da fiscalização.
 *
 * O `Content-Type` não é declarado aqui de propósito: o axios monta o `multipart/form-data` com o
 * `boundary` quando recebe um `FormData`, e escrever o cabeçalho à mão apagaria o `boundary` — o
 * servidor então recusa o arquivo inteiro com um erro que não fala de arquivo nenhum.
 *
 * Invalida TUDO de chamado ao concluir, e não só a lista: uma carga reescreve os chamados de um mês
 * e, com eles, o denominador dos indicadores daquela competência e a janela de cobertura do dado.
 * Guardar só a lista deixaria a tela de fechamento exibindo o número anterior à carga, que é
 * exatamente o número que alguém assina.
 *
 * @returns a mutação da carga
 */
export function useImportarPlanilha() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: async ({ arquivo, competencia }: EnvioDaPlanilha) => {
      const corpo = new FormData()
      corpo.append('arquivo', arquivo)
      if (competencia) {
        corpo.append('competencia', competencia)
      }
      // Timeout próprio: os 30s globais valem para consulta, não para subir um .xlsx em rede
      // lenta — abortar no meio viraria um "Sem conexão com o servidor" mentiroso.
      return (await api.post<RespostaDaCarga>('/chamados/importacoes', corpo, { timeout: 120_000 })).data
    },
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chaves.tudo() })
    },
  })
}

/**
 * A atualização automática pode rodar, e em que cadência?
 *
 * @returns a consulta da situação
 */
export function useSituacaoDaSincronizacao() {
  return useQuery({
    queryKey: chaves.sincronizacao(),
    queryFn: async () =>
      (await api.get<SituacaoDaSincronizacao>('/chamados/sincronizacoes/situacao')).data,
    staleTime: 60 * 1000,
  })
}

/**
 * Dispara uma rodada de atualização agora.
 *
 * Invalida tudo de chamado ao concluir, pela mesma razão da carga de planilha: uma rodada pode
 * reescrever o retrato corrente do mês inteiro.
 *
 * @returns a mutação da rodada
 */
export function useSincronizarAgora() {
  const cliente = useQueryClient()
  return useMutation({
    // A rodada varre a janela inteira no servidor; 30s de timeout global derrubariam uma rodada
    // legítima em rede lenta.
    mutationFn: async () =>
      (await api.post<RespostaDaCarga>('/chamados/sincronizacoes', null, { timeout: 120_000 })).data,
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chaves.tudo() })
    },
  })
}

/**
 * A análise por IA está disponível neste ambiente?
 *
 * @returns a consulta da situação
 */
export function useSituacaoDaIa() {
  return useQuery({
    queryKey: chaves.situacaoDaIa(),
    queryFn: async () => (await api.get<SituacaoDaIa>('/chamados/analises-ia/situacao')).data,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * O apoio da IA para este chamado — o parecer guardado e em que pé está a leitura.
 *
 * **Este GET pode agendar uma leitura no servidor**, e por isso a consulta é buscada de novo a
 * cada 4 segundos *enquanto* o estado for `PREPARANDO`, e só enquanto. Um `refetchInterval` fixo
 * transformaria a tela aberta num relógio batendo no servidor a vida inteira; sem nenhum, o
 * parecer que ficou pronto só apareceria se a pessoa recarregasse a página — e ela não tem por que
 * saber que precisa.
 *
 * @param id identificador interno do chamado
 * @returns a consulta do apoio
 */
export function useApoioDaIaNoChamado(id: number) {
  return useQuery({
    queryKey: chaves.pareceres(id),
    queryFn: async () => (await api.get<ApoioDaIaNoChamado>(`/chamados/${id}/analises-ia`)).data,
    enabled: Number.isFinite(id),
    refetchInterval: (consulta) => (consulta.state.data?.estado === 'PREPARANDO' ? 4000 : false),
  })
}

/**
 * Pede um parecer novo sobre o chamado.
 *
 * Invalida só os pareceres do chamado: um parecer não muda dado de chamado nenhum — é a Regra
 * nº 3 aparecendo até na invalidação de cache.
 *
 * @param id identificador interno do chamado
 * @returns a mutação da análise
 */
export function useAnalisarChamado(id: number) {
  const cliente = useQueryClient()
  return useMutation({
    // Modelo de raciocínio demora; o backend espera até 90s pela resposta.
    mutationFn: async () =>
      (await api.post<ParecerDaIa>(`/chamados/${id}/analises-ia`, null, { timeout: 120_000 })).data,
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chaves.pareceres(id) })
    },
  })
}

/**
 * Registra o julgamento do fiscal sobre um chamado.
 *
 * Invalida o prefixo inteiro de propósito, e não só o detalhe: uma marcação muda o denominador do
 * mês, a contagem da tela de fechamento e a lista de chamados sem julgamento. Invalidar só o
 * chamado editado deixaria o fechamento exibindo o número anterior — que é o número que vai para o
 * termo de recebimento.
 *
 * @param id chamado julgado
 * @returns a mutação, que devolve o chamado já atualizado
 */
export function useRegistrarMarcacao(id: number) {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: async (marcacao: MarcacaoParaGravar) => {
      const { data } = await api.put<DetalheChamado>(`/chamados/${id}/marcacao`, marcacao)
      return data
    },
    onSuccess: (chamado) => {
      // O detalhe recebe a resposta direto: o servidor já devolveu o registro gravado, e refazer o
      // GET só para ver o mesmo dado faria a tela piscar entre dois valores idênticos.
      cliente.setQueryData(chaves.detalhe(id), chamado)
      void cliente.invalidateQueries({ queryKey: chaves.tudo() })
    },
    onError: () => {
      // Recarrega o detalhe quando a gravação falha. A falha que importa aqui é o 409
      // `JULGAMENTO_DESATUALIZADO`: alguém julgou este chamado enquanto o formulário estava
      // aberto, e o que a tela tem em mãos é a versão velha. Sem este refetch, a mensagem mandaria
      // "confira o julgamento vigente" ao lado do julgamento antigo — que é o problema, não a
      // resposta. Nas outras falhas o refetch é barato e devolve o mesmo dado.
      void cliente.invalidateQueries({ queryKey: chaves.detalhe(id) })
    },
  })
}

/**
 * O calendário do contrato conferido contra o do e-Niterói.
 *
 * `staleTime` longo de propósito: jornada e feriados mudam em escala de meses, e a consulta abre uma
 * sessão no e-Niterói a cada chamada. O que ela responde — "o relógio que carimbou o prazo é o mesmo
 * que apura a glosa?" — não muda entre dois cliques.
 *
 * @returns a conferência dos dois calendários
 */
export function useConferenciaDeCalendario() {
  return useQuery({
    queryKey: ['fiscalizacao', 'calendario'],
    queryFn: async () => {
      const { data } = await api.get<ConferenciaDeCalendario>('/fiscalizacao/calendario')
      return data
    },
    staleTime: 30 * 60 * 1000,
  })
}

/**
 * Fecha a competência: transforma a prévia em registro.
 *
 * Invalida a competência inteira, e não só o fechamento: a lista de chamados passa a recusar
 * julgamento naquele mês, e o botão de marcar precisa refletir isso sem exigir um F5.
 *
 * @param competencia mês no formato AAAA-MM
 * @returns a mutação
 */
export function useFecharCompetencia(competencia: string) {
  return useAtoDaCompetencia(competencia, (c) => api.post<ApuracaoNaTela>(`/fiscalizacao/${c}/fechar`))
}

/**
 * Reabre a competência. Só administrador, e com motivo.
 *
 * @param competencia mês
 * @returns a mutação
 */
export function useReabrirCompetencia(competencia: string) {
  return useAtoDaCompetencia(competencia, (c, motivo) =>
    api.post<ApuracaoNaTela>(`/fiscalizacao/${c}/reabrir`, { motivo }),
  )
}

/**
 * Registra a dispensa de sanções do período de ajuste.
 *
 * @param competencia mês
 * @returns a mutação
 */
export function useDispensarSancoes(competencia: string) {
  return useAtoDaCompetencia(competencia, (c, fundamento) =>
    api.post<ApuracaoNaTela>(`/fiscalizacao/${c}/dispensa`, { fundamento }),
  )
}

/**
 * Revoga a dispensa vigente.
 *
 * @param competencia mês
 * @returns a mutação
 */
export function useRevogarDispensa(competencia: string) {
  return useAtoDaCompetencia(competencia, (c) => api.delete<ApuracaoNaTela>(`/fiscalizacao/${c}/dispensa`))
}

/**
 * O que os quatro atos sobre a competência têm em comum: mandam, e invalidam tudo do mês.
 *
 * Uma função só porque a invalidação é a parte fácil de errar — quatro cópias divergem no dia em
 * que uma delas precisar limpar mais uma chave, e a que ficar para trás deixa a tela mostrando o
 * estado anterior sem nenhum erro à vista.
 */
function useAtoDaCompetencia(
  competencia: string,
  chamar: (competencia: string, texto: string) => Promise<{ data: ApuracaoNaTela }>,
) {
  const cliente = useQueryClient()
  return useMutation({
    // O parâmetro é sempre `string`, mesmo nos atos que não pedem texto (fechar, revogar): um tipo
    // só evita quatro assinaturas divergentes para a mesma coisa, e chamar com '' é explícito.
    mutationFn: async (texto: string) => (await chamar(competencia, texto)).data,
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chaves.tudo() })
    },
  })
}
